// Fleets (04 §5.6, §3.4) and fleet events (§7.4).
import { z } from 'zod';
import {
  idSchema,
  isoDateTimeSchema,
  resourcesSchema,
  shipCountsSchema,
  shipTypeSchema,
} from './common.js';
import { fleetDetectionSchema } from './military.js';

export const FLEET_MISSIONS = ['attack', 'raid', 'reinforce', 'transfer', 'mission'] as const;
export const fleetMissionSchema = z.enum(FLEET_MISSIONS);
export type FleetMission = z.infer<typeof fleetMissionSchema>;

export const FLEET_STATUSES = [
  'stationed',
  'in_transit',
  'returning',
  'waiting',
  'in_battle',
  'on_mission',
] as const;

export const shipListSchema = z
  .array(
    z.strictObject({
      shipType: shipTypeSchema,
      count: z.number().int().positive().max(1_000_000),
    })
  )
  .min(1)
  .max(14);
export type ShipList = z.infer<typeof shipListSchema>;

export const routeLegSchema = z.strictObject({
  from: idSchema,
  to: idSchema,
  mode: z.enum(['gate', 'direct']),
  seconds: z.number().int().nonnegative(),
});

export const routeSchema = z.strictObject({
  legs: z.array(routeLegSchema),
  exitSystemId: idSchema,
  exitsNetworkAfterS: z.number().int().nonnegative(),
  totalS: z.number().int().nonnegative(),
  fleetSpeedLyph: z.number().positive(),
});
export type RoutePreview = z.infer<typeof routeSchema>;

export const routePreviewRequestSchema = z.strictObject({
  originSystemId: idSchema,
  targetSystemId: idSchema,
  ships: shipListSchema,
  forceDirect: z.boolean().default(false),
});
export type RoutePreviewRequest = z.input<typeof routePreviewRequestSchema>;

export const dispatchRequestSchema = z.strictObject({
  originSystemId: idSchema,
  targetSystemId: idSchema,
  mission: fleetMissionSchema,
  ships: shipListSchema,
  cargo: resourcesSchema.default({ metal: 0, gas: 0, supplies: 0 }),
  forceDirect: z.boolean().default(false),
  confirmShieldLift: z.boolean().default(false),
  /** G-70: show this attack or raid in the alliance's Attacks tab. */
  shared: z.boolean().default(false),
});
export type DispatchRequest = z.input<typeof dispatchRequestSchema>;

/** A fleet as its owner sees it (04 §3.4 "own fleet, any status"). */
export const ownFleetSchema = z.strictObject({
  id: idSchema,
  status: z.enum(FLEET_STATUSES),
  mission: fleetMissionSchema.nullable(),
  isGarrison: z.boolean(),
  originSystemId: idSchema.nullable(),
  targetSystemId: idSchema.nullable(),
  locationSystemId: idSchema.nullable(),
  ships: shipCountsSchema,
  stealthShips: shipCountsSchema,
  cargo: resourcesSchema,
  departedAt: isoDateTimeSchema.nullable(),
  exitsNetworkAt: isoDateTimeSchema.nullable(),
  arrivesAt: isoDateTimeSchema.nullable(),
  route: z.array(routeLegSchema).nullable(),
  battleId: idSchema.nullable(),
  /** G-70: listed in the alliance's Attacks tab while in transit. */
  shared: z.boolean(),
});
export type OwnFleet = z.infer<typeof ownFleetSchema>;

export const dispatchResponseSchema = z.strictObject({
  fleet: ownFleetSchema,
  shieldLifted: z.boolean(),
});
export type DispatchResponse = z.infer<typeof dispatchResponseSchema>;

export const fleetMovementResponseSchema = z.strictObject({
  fleetId: idSchema,
  status: z.literal('returning'),
  arrivesAt: isoDateTimeSchema,
});
export type FleetMovementResponse = z.infer<typeof fleetMovementResponseSchema>;

/** `PATCH G/fleets/{id}` and `PATCH G/fleet-orders/{id}` (G-70). */
export const shareRequestSchema = z.strictObject({ shared: z.boolean() });
export type ShareRequest = z.infer<typeof shareRequestSchema>;

export const fleetListResponseSchema = z.strictObject({ items: z.array(ownFleetSchema) });
export type FleetListResponse = z.infer<typeof fleetListResponseSchema>;

/**
 * `GET G/fleets/visible`: fleets in flight the viewer may see (04 §3.4) — own fleets, alliance
 * members' fleets for Commander+ (same view as the owner's, with the owner named), and detected
 * hostile fleets heading to own or (Commander+) alliance systems. Partners are cut from the slice.
 */
export const visibleFleetSchema = z.discriminatedUnion('relation', [
  z.strictObject({ relation: z.literal('own'), fleet: ownFleetSchema }),
  z.strictObject({
    relation: z.literal('alliance'),
    owner: z.strictObject({ playerId: idSchema, displayName: z.string() }),
    fleet: ownFleetSchema,
  }),
  z.strictObject({ relation: z.literal('detected'), detection: fleetDetectionSchema }),
]);
export type VisibleFleet = z.infer<typeof visibleFleetSchema>;

export const visibleFleetsResponseSchema = z.strictObject({ items: z.array(visibleFleetSchema) });
export type VisibleFleetsResponse = z.infer<typeof visibleFleetsResponseSchema>;

export const FLEET_ARRIVAL_OUTCOMES = [
  'merged',
  'stationed',
  'battle',
  'waiting',
  'raid',
  'returned',
  'mission_started',
] as const;

export const fleetArrivedSchema = z.strictObject({
  fleetId: idSchema,
  systemId: idSchema,
  outcome: z.enum(FLEET_ARRIVAL_OUTCOMES),
});

export const fleetReturnedSchema = z.strictObject({
  fleetId: idSchema,
  systemId: idSchema,
  cargo: resourcesSchema,
});

export const fleetIncomingCancelledSchema = z.strictObject({
  fleetId: idSchema,
  targetSystemId: idSchema,
});
